import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import {
  Box, TextField, Button, IconButton, Modal,
  Typography, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Select, MenuItem, InputLabel, FormControl, Checkbox, FormControlLabel, Divider,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useDispatch, useSelector } from 'react-redux';
import { GetListUsers, CreateUser, UpdateUser, DeleteUser, GetUserRoles, GetUserBusinessLines, SetUserBusinessLines } from '../../Redux/UsersDucks';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';
import '../../styles/AppStyle.css';

const isSuperAdmin = () => {
  const cookies = document.cookie.split('; ');
  const perfilCookie = cookies.find(c => c.startsWith('perfil='));
  return perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) === 'Super-Admin' : false;
};

const UserPage = () => {
  const dispatch = useDispatch();
  const superAdmin = isSuperAdmin();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState(0);
  const [selectedBusinessLines, setSelectedBusinessLines] = useState([]);
  const [loadingBL, setLoadingBL] = useState(false);
  const [savingBL, setSavingBL] = useState(false);

  const userList = useSelector(store => store.users.listUser);
  const userRoles = useSelector(store => store.users.listRol);
  const businessLines = useSelector(store => store.business);

  useEffect(() => {
    fetchUsers();
    dispatch(fetchBusinessesFromAPI());
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      dispatch(GetListUsers());
      dispatch(GetUserRoles());
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los usuarios');
      setLoading(false);
    }
  };

  const getStatusText = (active) => {
    return active ? 'Activo' : 'Inactivo';
  };

  const getRolText = (roleId) => {  
    const rol = userRoles.list.find(rol => rol.id === roleId);
    return rol ? rol.name : '';
  };

  const handleEdit = async (user) => {
    setSelectedUser(user);
    setName(user.nombre);
    setIdentifier(user.identifier);
    setEmail(user.email);
    setTelephone(user.telephone);
    setRol(user.roleId);
    setSelectedBusinessLines([]);
    setIsModalOpen(true);
    // Load assigned business lines
    setLoadingBL(true);
    const ids = await dispatch(GetUserBusinessLines(user.id));
    setSelectedBusinessLines(ids || []);
    setLoadingBL(false);
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };
  
  const handleReset = (user) => {
    setSelectedUser(user);
    setIsResetPasswordOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(DeleteUser(userToDelete.id));
      setIsDeleteDialogOpen(false);
      setTimeout(() => fetchUsers(), 300);
    } catch (err) {
      setError('Error al eliminar usuario');
    }
  };

  const confirmReset = async () => {
    try {
      //await userService.resetPassword(selectedUser.id, selectedUser.identifier);
      setIsResetPasswordOpen(false);
      fetchUsers();
      //toast.success('Contraseña reseteada con éxito');
    } catch (err) {
      setError('Error al resetear clave usuario');
    }
  };

  const handleViewDetail = (user) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setName('');
    setIdentifier('');
    setEmail('');
    setTelephone('');
    setPassword('');
    setRol(0);
    setSelectedBusinessLines([]);
    setIsModalOpen(true);
  };

  const handleSaveUser = async () => {
    const userDTO = {
      nombre: name,
      identifier,
      email,
      telephone,
      roleId: rol,
    };

    try {
      if (selectedUser) {
        userDTO.id = selectedUser.id;
        dispatch(UpdateUser(userDTO));
        // Save business lines for existing user
        setSavingBL(true);
        await dispatch(SetUserBusinessLines(selectedUser.id, selectedBusinessLines));
        setSavingBL(false);
      } else {
        dispatch(CreateUser(userDTO));
      }
      setTimeout(() => {
        fetchUsers();
        setIsModalOpen(false);
      }, 500);
    } catch (err) {
      setError('Error al guardar el usuario');
      setSavingBL(false);
    }
  };

  const handleToggleBusinessLine = (blId) => {
    setSelectedBusinessLines(prev =>
      prev.includes(blId) ? prev.filter(id => id !== blId) : [...prev, blId]
    );
  };

  const handleChangeOption = (event) => {
    const { value } = event.target;
    setRol(value);
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
    },
    {
      name: 'Nombre',
      selector: row => row.nombre,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Teléfono',
      selector: row => row.telephone,
      sortable: true,
    },
    {
      name: 'Estado',
      selector: row => getStatusText(row.active),
      sortable: true,
      cell: row => (
        <span className={`apple-badge ${row.active ? 'apple-badge-success' : 'apple-badge-error'}`}>
          {getStatusText(row.active)}
        </span>
      ),
    },
    {
      name: 'Acciones',
      cell: row => (
        <Box>
          <IconButton onClick={() => handleViewDetail(row)} size="small" className="apple-button apple-tooltip">
            <VisibilityIcon fontSize="small" />
            <span className="tooltiptext">Ver Detalle</span>
          </IconButton>
          <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
            <EditIcon fontSize="small" />
            <span className="tooltiptext">Editar</span>
          </IconButton>
          <IconButton onClick={() => handleReset(row)} size="small" className="apple-button apple-tooltip">
            <LockResetIcon fontSize="small" />
            <span className="tooltiptext">Resetear Clave</span>
          </IconButton>
          {superAdmin && (
            <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
              <DeleteIcon fontSize="small" />
              <span className="tooltiptext">Eliminar</span>
            </IconButton>
          )}
        </Box>
      ),
      width: '200px', // Ajuste del ancho de la columna de acciones
    },
  ];

  const filteredItems = (userList.list || []).filter(
    item =>
        (item.nombre && item.nombre.toLowerCase().includes((filterText || "").toLowerCase())) ||
        (item.email && item.email.toLowerCase().includes((filterText || "").toLowerCase()))
  );

  const customStyles = {
    table: {
      style: {
        minWidth: '100%',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#F2F2F7',
        borderBottom: 'none',
      },
    },
    headCells: {
      style: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868B',
        textTransform: 'uppercase',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        color: '#1D1D1F',
      },
    },
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
        <div className="apple-search-container">
          <SearchIcon className="apple-search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre o email"
            onChange={e => setFilterText(e.target.value)}
            className="apple-search-input"
          />
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          className="apple-button"
        >
          Agregar Usuario
        </Button>
      </Box>
    );
  }, [filterText]);

  return (
    <div className="apple-container">
      <Paper className="apple-card">
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          progressPending={loading}
          persistTableHead
          customStyles={customStyles}
        />
      </Paper>

      {/* Modal para editar/crear usuario */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="apple-modal" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
        }}>
          <Typography id="modal-modal-title" className="apple-modal-title">
            {selectedUser ? 'Editar Usuario' : 'Agregar Usuario'}
          </Typography>

          <div className="apple-modal-content">
            <TextField
              label="Nombre"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Identificador"
              fullWidth
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
            />
            <TextField
              label="Teléfono"
              fullWidth
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              margin="normal"
            />
             <FormControl variant="outlined" className="apple-select" sx={{ mb:"8px" }} fullWidth size="medium">
              <InputLabel id="business-line-label">Rol de Perfil</InputLabel>
              <Select
                labelId="business-line-label"
                label="Línea de Negocio"
                value={rol}
                onChange={handleChangeOption}
              >
                <MenuItem value={0}>Seleccione rol</MenuItem>
                {userRoles.list.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedUser && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Líneas de negocio asignadas
                </Typography>
                {loadingBL ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                    {Array.isArray(businessLines) && businessLines.map(bl => (
                      <FormControlLabel
                        key={bl.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedBusinessLines.includes(bl.id)}
                            onChange={() => handleToggleBusinessLine(bl.id)}
                          />
                        }
                        label={<Typography variant="body2">{bl.siglas || bl.name || bl.nombre}</Typography>}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveUser}
              disabled={savingBL}
              className="apple-button"
              fullWidth
              sx={{ mt: 2 }}
            >
              {savingBL ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Dialog para eliminar usuario */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar al usuario "{userToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} className="apple-button">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para resetear contraseña */}
      <Dialog
        open={isResetDialogOpen}
        onClose={() => setIsResetPasswordOpen(false)}
      >
        <DialogTitle>Confirmar Reseteo de Clave</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea resetear la clave del usuario "{selectedUser?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResetPasswordOpen(false)} className="apple-button">
            Cancelar
          </Button>
          <Button onClick={confirmReset} className="apple-button">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para ver detalle del usuario */}
      <Modal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="apple-modal" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
        }}>
          <Typography id="modal-modal-title" className="apple-modal-title">
            Detalles del Usuario
          </Typography>
    
          <div className="apple-modal-content">
            <TextField
              label="Nombre"
              fullWidth
              value={selectedUser?.nombre}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Identificador"
              fullWidth
              value={selectedUser?.identifier}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Email"
              fullWidth
              value={selectedUser?.email}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Teléfono"
              fullWidth
              value={selectedUser?.telephone}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Rol"
              fullWidth
              value={getRolText(selectedUser?.roleId)}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Estado"
              fullWidth
              value={getStatusText(selectedUser?.idStatus)}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
          <div className="apple-modal-footer">
            <Button onClick={() => setIsDetailModalOpen(false)} className="apple-button">
              Cerrar
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default UserPage;
